import { api } from '../../../services/api';
import { Department, Employee, StrategicPlan, AnnualCorporateScorecard, AssessmentPeriod, ContractingPeriod, PerformanceRating, Perspective, StrategicObjective, KPI, StrategicInitiative, InitiativeKPI, StandardInitiative, StandardInitiativeKPI, AssessmentFrequency, ScorecardPeriodStatus } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

export const strategicPerformanceSettingsAPI = {
  // Department APIs
  departments: {
    getAll: async (): Promise<Department[]> => {
      const response = await api.get<any>('/old-strategic-performance-settings/departments');
      // Backend returns { success: true, data: [...] }
      // After interceptor: response.data = { success: true, data: [...] }
      const backendData = response.data?.data || response.data;
      const departments = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return departments.map((dept: any) => ({
        id: dept._id || dept.id,
        name: dept.name,
        description: dept.description,
      }));
    },
    getById: async (id: string): Promise<Department> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/departments/${id}`);
      const dept = response.data?.data || response.data;
      return {
        id: dept._id || dept.id,
        name: dept.name,
        description: dept.description,
      };
    },
    create: async (data: { name: string; description: string }): Promise<Department> => {
      const response = await api.post<any>('/old-strategic-performance-settings/departments', data);
      const dept = response.data?.data || response.data;
      return {
        id: dept._id || dept.id,
        name: dept.name,
        description: dept.description,
      };
    },
    update: async (id: string, data: { name: string; description: string }): Promise<Department> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/departments/${id}`, data);
      const dept = response.data?.data || response.data;
      return {
        id: dept._id || dept.id,
        name: dept.name,
        description: dept.description,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/departments/${id}`);
    },
  },

  // Employee APIs
  employees: {
    getAll: async (): Promise<Employee[]> => {
      const response = await api.get<any>('/old-strategic-performance-settings/employees');
      // Backend returns { success: true, data: [...] } with populated userId and departmentId
      const backendData = response.data?.data || response.data;
      const employees = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return employees.map((emp: any) => {
        const userId = typeof emp.userId === 'string' 
          ? emp.userId 
          : (emp.userId?._id || emp.userId?.id || emp.userId);
        const user = emp.userId && typeof emp.userId === 'object' ? emp.userId : null;
        const departmentId = typeof emp.departmentId === 'string' 
          ? emp.departmentId 
          : (emp.departmentId?._id || emp.departmentId?.id || emp.departmentId);
        
        return {
          id: emp._id || emp.id,
          userId: userId || '',
          microsoftId: user?.MicrosoftId || user?.microsoftId || '', // Extract MicrosoftId
          fullName: user?.name || '',
          jobTitle: user?.jobTitle || '',
          email: user?.email || '',
          departmentId: departmentId || '',
          isDepartmentHead: emp.isDepartmentHead || false,
          isCEO: emp.isCEO || false,
          status: emp.status || 'active',
        };
      });
    },
    getById: async (id: string): Promise<Employee> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/employees/${id}`);
      const emp = response.data?.data || response.data;
      const userId = typeof emp.userId === 'string' 
        ? emp.userId 
        : (emp.userId?._id || emp.userId?.id || emp.userId);
      const user = emp.userId && typeof emp.userId === 'object' ? emp.userId : null;
      const departmentId = typeof emp.departmentId === 'string' 
        ? emp.departmentId 
        : (emp.departmentId?._id || emp.departmentId?.id || emp.departmentId);
      
      return {
        id: emp._id || emp.id,
        userId: userId || '',
        fullName: user?.name || '',
        jobTitle: user?.jobTitle || '',
        email: user?.email || '',
        departmentId: departmentId || '',
        isDepartmentHead: emp.isDepartmentHead || false,
        isCEO: emp.isCEO || false,
        status: emp.status || 'active',
      };
    },
    create: async (data: {
      userId: string;
      departmentId: string;
      isDepartmentHead: boolean;
      isCEO: boolean;
      status: 'active' | 'inactive';
    }): Promise<Employee> => {
      const response = await api.post<any>('/old-strategic-performance-settings/employees', data);
      const emp = response.data?.data || response.data;
      const userId = typeof emp.userId === 'string' 
        ? emp.userId 
        : (emp.userId?._id || emp.userId?.id || emp.userId);
      const user = emp.userId && typeof emp.userId === 'object' ? emp.userId : null;
      const departmentId = typeof emp.departmentId === 'string' 
        ? emp.departmentId 
        : (emp.departmentId?._id || emp.departmentId?.id || emp.departmentId);
      
      return {
        id: emp._id || emp.id,
        userId: userId || '',
        fullName: user?.name || '',
        jobTitle: user?.jobTitle || '',
        email: user?.email || '',
        departmentId: departmentId || '',
        isDepartmentHead: emp.isDepartmentHead || false,
        isCEO: emp.isCEO || false,
        status: emp.status || 'active',
      };
    },
    update: async (id: string, data: {
      userId?: string;
      departmentId?: string;
      isDepartmentHead?: boolean;
      isCEO?: boolean;
      status?: 'active' | 'inactive';
    }): Promise<Employee> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/employees/${id}`, data);
      const emp = response.data?.data || response.data;
      const userId = typeof emp.userId === 'string' 
        ? emp.userId 
        : (emp.userId?._id || emp.userId?.id || emp.userId);
      const user = emp.userId && typeof emp.userId === 'object' ? emp.userId : null;
      const departmentId = typeof emp.departmentId === 'string' 
        ? emp.departmentId 
        : (emp.departmentId?._id || emp.departmentId?.id || emp.departmentId);
      
      return {
        id: emp._id || emp.id,
        userId: userId || '',
        fullName: user?.name || '',
        jobTitle: user?.jobTitle || '',
        email: user?.email || '',
        departmentId: departmentId || '',
        isDepartmentHead: emp.isDepartmentHead || false,
        isCEO: emp.isCEO || false,
        status: emp.status || 'active',
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/employees/${id}`);
    },
  },

  // Strategic Plan APIs
  strategicPlans: {
    getAll: async (status?: 'Active' | 'Not Active'): Promise<StrategicPlan[]> => {
      const params = status ? { status } : {};
      const response = await api.get<any>('/old-strategic-performance-settings/strategic-plans', { params });
      const backendData = response.data?.data || response.data;
      const plans = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return plans.map((plan: any) => ({
        id: plan._id || plan.id,
        description: plan.description,
        startDate: plan.startDate ? new Date(plan.startDate).toISOString().split('T')[0] : '',
        endDate: plan.endDate ? new Date(plan.endDate).toISOString().split('T')[0] : '',
        status: plan.status || 'Not Active',
      }));
    },
    getById: async (id: string): Promise<StrategicPlan> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/strategic-plans/${id}`);
      const plan = response.data?.data || response.data;
      return {
        id: plan._id || plan.id,
        description: plan.description,
        startDate: plan.startDate ? new Date(plan.startDate).toISOString().split('T')[0] : '',
        endDate: plan.endDate ? new Date(plan.endDate).toISOString().split('T')[0] : '',
        status: plan.status || 'Not Active',
      };
    },
    create: async (data: {
      description: string;
      startDate: string;
      endDate: string;
      status: 'Active' | 'Not Active';
    }): Promise<StrategicPlan> => {
      const response = await api.post<any>('/old-strategic-performance-settings/strategic-plans', data);
      const plan = response.data?.data || response.data;
      return {
        id: plan._id || plan.id,
        description: plan.description,
        startDate: plan.startDate ? new Date(plan.startDate).toISOString().split('T')[0] : '',
        endDate: plan.endDate ? new Date(plan.endDate).toISOString().split('T')[0] : '',
        status: plan.status || 'Not Active',
      };
    },
    update: async (id: string, data: {
      description?: string;
      startDate?: string;
      endDate?: string;
      status?: 'Active' | 'Not Active';
    }): Promise<StrategicPlan> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/strategic-plans/${id}`, data);
      const plan = response.data?.data || response.data;
      return {
        id: plan._id || plan.id,
        description: plan.description,
        startDate: plan.startDate ? new Date(plan.startDate).toISOString().split('T')[0] : '',
        endDate: plan.endDate ? new Date(plan.endDate).toISOString().split('T')[0] : '',
        status: plan.status || 'Not Active',
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/strategic-plans/${id}`);
    },
  },

  // Annual Corporate Scorecard APIs
  annualCorporateScorecards: {
    getAll: async (): Promise<AnnualCorporateScorecard[]> => {
      const response = await api.get<any>('/old-strategic-performance-settings/annual-corporate-scorecards');
      const backendData = response.data?.data || response.data;
      const scorecards = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return scorecards.map((scorecard: any) => {
        const planId = typeof scorecard.planId === 'string' 
          ? scorecard.planId 
          : (scorecard.planId?._id || scorecard.planId?.id || scorecard.planId);
        
        return {
          id: scorecard._id || scorecard.id,
          name: scorecard.name,
          planId: planId || '',
          startDate: scorecard.startDate ? new Date(scorecard.startDate).toISOString().split('T')[0] : '',
          endDate: scorecard.endDate ? new Date(scorecard.endDate).toISOString().split('T')[0] : '',
          status: scorecard.status || 'Not Active',
        };
      });
    },
    getById: async (id: string): Promise<AnnualCorporateScorecard> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/annual-corporate-scorecards/${id}`);
      const scorecard = response.data?.data || response.data;
      const planId = typeof scorecard.planId === 'string' 
        ? scorecard.planId 
        : (scorecard.planId?._id || scorecard.planId?.id || scorecard.planId);
      
      return {
        id: scorecard._id || scorecard.id,
        name: scorecard.name,
        planId: planId || '',
        startDate: scorecard.startDate ? new Date(scorecard.startDate).toISOString().split('T')[0] : '',
        endDate: scorecard.endDate ? new Date(scorecard.endDate).toISOString().split('T')[0] : '',
        status: scorecard.status || 'Not Active',
      };
    },
    create: async (data: {
      name: string;
      planId: string;
      startDate: string;
      endDate: string;
      status?: 'Active' | 'Not Active';
    }): Promise<AnnualCorporateScorecard> => {
      const response = await api.post<any>('/old-strategic-performance-settings/annual-corporate-scorecards', data);
      const scorecard = response.data?.data || response.data;
      const planId = typeof scorecard.planId === 'string' 
        ? scorecard.planId 
        : (scorecard.planId?._id || scorecard.planId?.id || scorecard.planId);
      
      return {
        id: scorecard._id || scorecard.id,
        name: scorecard.name,
        planId: planId || '',
        startDate: scorecard.startDate ? new Date(scorecard.startDate).toISOString().split('T')[0] : '',
        endDate: scorecard.endDate ? new Date(scorecard.endDate).toISOString().split('T')[0] : '',
        status: scorecard.status || 'Not Active',
      };
    },
    update: async (id: string, data: {
      name?: string;
      planId?: string;
      startDate?: string;
      endDate?: string;
      status?: 'Active' | 'Not Active';
    }): Promise<AnnualCorporateScorecard> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/annual-corporate-scorecards/${id}`, data);
      const scorecard = response.data?.data || response.data;
      const planId = typeof scorecard.planId === 'string' 
        ? scorecard.planId 
        : (scorecard.planId?._id || scorecard.planId?.id || scorecard.planId);
      
      return {
        id: scorecard._id || scorecard.id,
        name: scorecard.name,
        planId: planId || '',
        startDate: scorecard.startDate ? new Date(scorecard.startDate).toISOString().split('T')[0] : '',
        endDate: scorecard.endDate ? new Date(scorecard.endDate).toISOString().split('T')[0] : '',
        status: scorecard.status || 'Not Active',
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/annual-corporate-scorecards/${id}`);
    },
  },
  assessmentPeriods: {
    getAll: async (scorecardId: string): Promise<AssessmentPeriod[]> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/assessment-periods/${scorecardId}`);
      const backendData = response.data?.data || response.data;
      const periods = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return periods.map((period: any) => ({
        id: period._id || period.id,
        scorecardId: typeof period.scorecardId === 'string' 
          ? period.scorecardId 
          : (period.scorecardId?._id || period.scorecardId?.id || period.scorecardId),
        frequency: period.frequency,
        periodNumber: period.periodNumber,
        startDate: period.startDate ? new Date(period.startDate).toISOString().split('T')[0] : '',
        endDate: period.endDate ? new Date(period.endDate).toISOString().split('T')[0] : '',
      }));
    },
    createOrUpdate: async (data: {
      scorecardId: string;
      frequency: 'Quarterly' | 'Bi-Annually' | 'Annually';
      periodNumber: number;
      startDate: string;
      endDate: string;
    }): Promise<AssessmentPeriod> => {
      const response = await api.post<any>('/old-strategic-performance-settings/assessment-periods', data);
      const period = response.data?.data || response.data;
      return {
        id: period._id || period.id,
        scorecardId: typeof period.scorecardId === 'string' 
          ? period.scorecardId 
          : (period.scorecardId?._id || period.scorecardId?.id || period.scorecardId),
        frequency: period.frequency,
        periodNumber: period.periodNumber,
        startDate: period.startDate ? new Date(period.startDate).toISOString().split('T')[0] : '',
        endDate: period.endDate ? new Date(period.endDate).toISOString().split('T')[0] : '',
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/assessment-periods/${id}`);
    },
  },
  contractingPeriods: {
    getAll: async (scorecardId: string): Promise<ContractingPeriod[]> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/contracting-periods/${scorecardId}`);
      const backendData = response.data?.data || response.data;
      const periods = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return periods.map((period: any) => ({
        id: period._id || period.id,
        scorecardId: typeof period.scorecardId === 'string' 
          ? period.scorecardId 
          : (period.scorecardId?._id || period.scorecardId?.id || period.scorecardId),
        frequency: period.frequency,
        periodNumber: period.periodNumber,
        startDate: period.startDate ? new Date(period.startDate).toISOString().split('T')[0] : '',
        endDate: period.endDate ? new Date(period.endDate).toISOString().split('T')[0] : '',
      }));
    },
    createOrUpdate: async (data: {
      scorecardId: string;
      frequency: 'Quarterly' | 'Bi-Annually' | 'Annually';
      periodNumber: number;
      startDate: string;
      endDate: string;
    }): Promise<ContractingPeriod> => {
      const response = await api.post<any>('/old-strategic-performance-settings/contracting-periods', data);
      const period = response.data?.data || response.data;
      return {
        id: period._id || period.id,
        scorecardId: typeof period.scorecardId === 'string' 
          ? period.scorecardId 
          : (period.scorecardId?._id || period.scorecardId?.id || period.scorecardId),
        frequency: period.frequency,
        periodNumber: period.periodNumber,
        startDate: period.startDate ? new Date(period.startDate).toISOString().split('T')[0] : '',
        endDate: period.endDate ? new Date(period.endDate).toISOString().split('T')[0] : '',
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/contracting-periods/${id}`);
    },
  },
  performanceRatings: {
    getAll: async (planId?: string, scorecardId?: string): Promise<PerformanceRating[]> => {
      const params = new URLSearchParams();
      if (planId) params.append('planId', planId);
      if (scorecardId) params.append('scorecardId', scorecardId);
      
      const response = await api.get<any>(`/old-strategic-performance-settings/performance-ratings?${params.toString()}`);
      const backendData = response.data?.data || response.data;
      const ratings = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return ratings.map((rating: any) => {
        const planIdValue = typeof rating.planId === 'string' 
          ? rating.planId 
          : (rating.planId?._id || rating.planId?.id || rating.planId || undefined);
        const scorecardIdValue = typeof rating.scorecardId === 'string' 
          ? rating.scorecardId 
          : (rating.scorecardId?._id || rating.scorecardId?.id || rating.scorecardId || undefined);
        
        return {
          id: rating._id || rating.id,
          planId: planIdValue,
          scorecardId: scorecardIdValue,
          score: rating.score,
          name: rating.name,
          description: rating.description,
          min: rating.min,
          max: rating.max,
          color: rating.color,
        };
      });
    },
    getById: async (id: string): Promise<PerformanceRating> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/performance-ratings/${id}`);
      const rating = response.data?.data || response.data;
      const planIdValue = typeof rating.planId === 'string' 
        ? rating.planId 
        : (rating.planId?._id || rating.planId?.id || rating.planId || undefined);
      const scorecardIdValue = typeof rating.scorecardId === 'string' 
        ? rating.scorecardId 
        : (rating.scorecardId?._id || rating.scorecardId?.id || rating.scorecardId || undefined);
      
      return {
        id: rating._id || rating.id,
        planId: planIdValue,
        scorecardId: scorecardIdValue,
        score: rating.score,
        name: rating.name,
        description: rating.description,
        min: rating.min,
        max: rating.max,
        color: rating.color,
      };
    },
    create: async (data: {
      planId?: string;
      scorecardId?: string;
      score: number;
      name: string;
      description: string;
      min: string;
      max: string;
      color: string;
    }): Promise<PerformanceRating> => {
      const response = await api.post<any>('/old-strategic-performance-settings/performance-ratings', data);
      const rating = response.data?.data || response.data;
      const planIdValue = typeof rating.planId === 'string' 
        ? rating.planId 
        : (rating.planId?._id || rating.planId?.id || rating.planId || undefined);
      const scorecardIdValue = typeof rating.scorecardId === 'string' 
        ? rating.scorecardId 
        : (rating.scorecardId?._id || rating.scorecardId?.id || rating.scorecardId || undefined);
      
      return {
        id: rating._id || rating.id,
        planId: planIdValue,
        scorecardId: scorecardIdValue,
        score: rating.score,
        name: rating.name,
        description: rating.description,
        min: rating.min,
        max: rating.max,
        color: rating.color,
      };
    },
    update: async (id: string, data: {
      score?: number;
      name?: string;
      description?: string;
      min?: string;
      max?: string;
      color?: string;
    }): Promise<PerformanceRating> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/performance-ratings/${id}`, data);
      const rating = response.data?.data || response.data;
      const planIdValue = typeof rating.planId === 'string' 
        ? rating.planId 
        : (rating.planId?._id || rating.planId?.id || rating.planId || undefined);
      const scorecardIdValue = typeof rating.scorecardId === 'string' 
        ? rating.scorecardId 
        : (rating.scorecardId?._id || rating.scorecardId?.id || rating.scorecardId || undefined);
      
      return {
        id: rating._id || rating.id,
        planId: planIdValue,
        scorecardId: scorecardIdValue,
        score: rating.score,
        name: rating.name,
        description: rating.description,
        min: rating.min,
        max: rating.max,
        color: rating.color,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/performance-ratings/${id}`);
    },
    copyFromPlanToScorecard: async (planId: string, scorecardId: string): Promise<PerformanceRating[]> => {
      const response = await api.post<any>('/old-strategic-performance-settings/performance-ratings/copy-from-plan', {
        planId,
        scorecardId
      });
      const backendData = response.data?.data || response.data;
      const ratings = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return ratings.map((rating: any) => {
        const planIdValue = typeof rating.planId === 'string' 
          ? rating.planId 
          : (rating.planId?._id || rating.planId?.id || rating.planId || undefined);
        const scorecardIdValue = typeof rating.scorecardId === 'string' 
          ? rating.scorecardId 
          : (rating.scorecardId?._id || rating.scorecardId?.id || rating.scorecardId || undefined);
        
        return {
          id: rating._id || rating.id,
          planId: planIdValue,
          scorecardId: scorecardIdValue,
          score: rating.score,
          name: rating.name,
          description: rating.description,
          min: rating.min,
          max: rating.max,
          color: rating.color,
        };
      });
    },
  },
  perspectives: {
    getAll: async (planId: string): Promise<Perspective[]> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/perspectives?planId=${planId}`);
      const backendData = response.data?.data || response.data;
      const perspectives = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return perspectives.map((p: any) => ({
        id: p._id || p.id,
        planId: typeof p.planId === 'string' 
          ? p.planId 
          : (p.planId?._id || p.planId?.id || p.planId),
        name: p.name,
        description: p.description || '',
      }));
    },
    getById: async (id: string): Promise<Perspective> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/perspectives/${id}`);
      const p = response.data?.data || response.data;
      return {
        id: p._id || p.id,
        planId: typeof p.planId === 'string' 
          ? p.planId 
          : (p.planId?._id || p.planId?.id || p.planId),
        name: p.name,
        description: p.description || '',
      };
    },
    create: async (data: { planId: string; name: string; description?: string }): Promise<Perspective> => {
      const response = await api.post<any>('/old-strategic-performance-settings/perspectives', data);
      const p = response.data?.data || response.data;
      return {
        id: p._id || p.id,
        planId: typeof p.planId === 'string' 
          ? p.planId 
          : (p.planId?._id || p.planId?.id || p.planId),
        name: p.name,
        description: p.description || '',
      };
    },
    update: async (id: string, data: { name?: string; description?: string }): Promise<Perspective> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/perspectives/${id}`, data);
      const p = response.data?.data || response.data;
      return {
        id: p._id || p.id,
        planId: typeof p.planId === 'string' 
          ? p.planId 
          : (p.planId?._id || p.planId?.id || p.planId),
        name: p.name,
        description: p.description || '',
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/perspectives/${id}`);
    },
  },
  strategicObjectives: {
    getAll: async (planId?: string, scorecardId?: string, quarter?: string): Promise<StrategicObjective[]> => {
      const params = new URLSearchParams();
      if (planId) params.append('planId', planId);
      if (scorecardId) params.append('scorecardId', scorecardId);
      if (quarter) params.append('quarter', quarter);
      
      const response = await api.get<any>(`/old-strategic-performance-settings/strategic-objectives?${params.toString()}`);
      const backendData = response.data?.data || response.data;
      const objectives = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return objectives.map((obj: any) => ({
        id: obj._id || obj.id,
        planId: obj.planId ? (typeof obj.planId === 'string' ? obj.planId : (obj.planId?._id || obj.planId?.id)) : undefined,
        scorecardId: obj.scorecardId ? (typeof obj.scorecardId === 'string' ? obj.scorecardId : (obj.scorecardId?._id || obj.scorecardId?.id)) : undefined,
        quarter: obj.quarter,
        perspectiveId: typeof obj.perspectiveId === 'string' 
          ? obj.perspectiveId 
          : (obj.perspectiveId?._id || obj.perspectiveId?.id || obj.perspectiveId),
        title: obj.title,
        createdAt: obj.createdAt ? (typeof obj.createdAt === 'string' ? obj.createdAt : obj.createdAt.toISOString?.() || new Date(obj.createdAt).toISOString()) : undefined,
      }));
    },
    getById: async (id: string): Promise<StrategicObjective> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/strategic-objectives/${id}`);
      const obj = response.data?.data || response.data;
      return {
        id: obj._id || obj.id,
        planId: obj.planId ? (typeof obj.planId === 'string' ? obj.planId : (obj.planId?._id || obj.planId?.id)) : undefined,
        scorecardId: obj.scorecardId ? (typeof obj.scorecardId === 'string' ? obj.scorecardId : (obj.scorecardId?._id || obj.scorecardId?.id)) : undefined,
        quarter: obj.quarter,
        perspectiveId: typeof obj.perspectiveId === 'string' 
          ? obj.perspectiveId 
          : (obj.perspectiveId?._id || obj.perspectiveId?.id || obj.perspectiveId),
        title: obj.title,
        createdAt: obj.createdAt ? (typeof obj.createdAt === 'string' ? obj.createdAt : obj.createdAt.toISOString?.() || new Date(obj.createdAt).toISOString()) : undefined,
      };
    },
    create: async (data: {
      planId?: string;
      scorecardId?: string;
      quarter?: string;
      perspectiveId: string;
      title: string;
    }): Promise<StrategicObjective> => {
      const response = await api.post<any>('/old-strategic-performance-settings/strategic-objectives', data);
      const obj = response.data?.data || response.data;
      return {
        id: obj._id || obj.id,
        planId: obj.planId ? (typeof obj.planId === 'string' ? obj.planId : (obj.planId?._id || obj.planId?.id)) : undefined,
        scorecardId: obj.scorecardId ? (typeof obj.scorecardId === 'string' ? obj.scorecardId : (obj.scorecardId?._id || obj.scorecardId?.id)) : undefined,
        quarter: obj.quarter,
        perspectiveId: typeof obj.perspectiveId === 'string' 
          ? obj.perspectiveId 
          : (obj.perspectiveId?._id || obj.perspectiveId?.id || obj.perspectiveId),
        title: obj.title,
        createdAt: obj.createdAt ? (typeof obj.createdAt === 'string' ? obj.createdAt : obj.createdAt.toISOString?.() || new Date(obj.createdAt).toISOString()) : undefined,
      };
    },
    update: async (id: string, data: { perspectiveId?: string; title?: string }): Promise<StrategicObjective> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/strategic-objectives/${id}`, data);
      const obj = response.data?.data || response.data;
      return {
        id: obj._id || obj.id,
        planId: obj.planId ? (typeof obj.planId === 'string' ? obj.planId : (obj.planId?._id || obj.planId?.id)) : undefined,
        scorecardId: obj.scorecardId ? (typeof obj.scorecardId === 'string' ? obj.scorecardId : (obj.scorecardId?._id || obj.scorecardId?.id)) : undefined,
        quarter: obj.quarter,
        perspectiveId: typeof obj.perspectiveId === 'string' 
          ? obj.perspectiveId 
          : (obj.perspectiveId?._id || obj.perspectiveId?.id || obj.perspectiveId),
        title: obj.title,
        createdAt: obj.createdAt ? (typeof obj.createdAt === 'string' ? obj.createdAt : obj.createdAt.toISOString?.() || new Date(obj.createdAt).toISOString()) : undefined,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/strategic-objectives/${id}`);
    },
  },
  kpis: {
    getAll: async (objectiveId: string): Promise<KPI[]> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/kpis?objectiveId=${objectiveId}`);
      const backendData = response.data?.data || response.data;
      const kpis = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return kpis.map((kpi: any) => ({
        id: kpi._id || kpi.id,
        objectiveId: typeof kpi.objectiveId === 'string' 
          ? kpi.objectiveId 
          : (kpi.objectiveId?._id || kpi.objectiveId?.id || kpi.objectiveId),
        name: kpi.name,
        weight: kpi.weight,
        baseline: kpi.baseline,
        target: kpi.target,
      }));
    },
    getById: async (id: string): Promise<KPI> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/kpis/${id}`);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        objectiveId: typeof kpi.objectiveId === 'string' 
          ? kpi.objectiveId 
          : (kpi.objectiveId?._id || kpi.objectiveId?.id || kpi.objectiveId),
        name: kpi.name,
        weight: kpi.weight,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    create: async (data: {
      objectiveId: string;
      name: string;
      weight: number;
      baseline: string;
      target: string;
    }): Promise<KPI> => {
      const response = await api.post<any>('/old-strategic-performance-settings/kpis', data);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        objectiveId: typeof kpi.objectiveId === 'string' 
          ? kpi.objectiveId 
          : (kpi.objectiveId?._id || kpi.objectiveId?.id || kpi.objectiveId),
        name: kpi.name,
        weight: kpi.weight,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    update: async (id: string, data: {
      name?: string;
      weight?: number;
      baseline?: string;
      target?: string;
    }): Promise<KPI> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/kpis/${id}`, data);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        objectiveId: typeof kpi.objectiveId === 'string' 
          ? kpi.objectiveId 
          : (kpi.objectiveId?._id || kpi.objectiveId?.id || kpi.objectiveId),
        name: kpi.name,
        weight: kpi.weight,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/kpis/${id}`);
    },
  },
  strategicInitiatives: {
    getAll: async (planId?: string, scorecardId?: string, quarter?: string): Promise<StrategicInitiative[]> => {
      const params = new URLSearchParams();
      if (planId) params.append('planId', planId);
      if (scorecardId) params.append('scorecardId', scorecardId);
      if (quarter) params.append('quarter', quarter);
      
      const response = await api.get<any>(`/old-strategic-performance-settings/strategic-initiatives?${params.toString()}`);
      const backendData = response.data?.data || response.data;
      const initiatives = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return initiatives.map((init: any) => ({
        id: init._id || init.id,
        planId: init.planId ? (typeof init.planId === 'string' ? init.planId : (init.planId?._id || init.planId?.id)) : undefined,
        scorecardId: init.scorecardId ? (typeof init.scorecardId === 'string' ? init.scorecardId : (init.scorecardId?._id || init.scorecardId?.id)) : undefined,
        quarter: init.quarter,
        objectiveId: typeof init.objectiveId === 'string' 
          ? init.objectiveId 
          : (init.objectiveId?._id || init.objectiveId?.id || init.objectiveId),
        title: init.title,
        owner: init.owner,
      }));
    },
    getById: async (id: string): Promise<StrategicInitiative> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/strategic-initiatives/${id}`);
      const init = response.data?.data || response.data;
      return {
        id: init._id || init.id,
        planId: init.planId ? (typeof init.planId === 'string' ? init.planId : (init.planId?._id || init.planId?.id)) : undefined,
        scorecardId: init.scorecardId ? (typeof init.scorecardId === 'string' ? init.scorecardId : (init.scorecardId?._id || init.scorecardId?.id)) : undefined,
        quarter: init.quarter,
        objectiveId: typeof init.objectiveId === 'string' 
          ? init.objectiveId 
          : (init.objectiveId?._id || init.objectiveId?.id || init.objectiveId),
        title: init.title,
        owner: init.owner,
      };
    },
    create: async (data: {
      planId?: string;
      scorecardId?: string;
      quarter?: string;
      objectiveId: string;
      title: string;
      owner: string;
    }): Promise<StrategicInitiative> => {
      const response = await api.post<any>('/old-strategic-performance-settings/strategic-initiatives', data);
      const init = response.data?.data || response.data;
      return {
        id: init._id || init.id,
        planId: init.planId ? (typeof init.planId === 'string' ? init.planId : (init.planId?._id || init.planId?.id)) : undefined,
        scorecardId: init.scorecardId ? (typeof init.scorecardId === 'string' ? init.scorecardId : (init.scorecardId?._id || init.scorecardId?.id)) : undefined,
        quarter: init.quarter,
        objectiveId: typeof init.objectiveId === 'string' 
          ? init.objectiveId 
          : (init.objectiveId?._id || init.objectiveId?.id || init.objectiveId),
        title: init.title,
        owner: init.owner,
      };
    },
    update: async (id: string, data: {
      objectiveId?: string;
      title?: string;
      owner?: string;
    }): Promise<StrategicInitiative> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/strategic-initiatives/${id}`, data);
      const init = response.data?.data || response.data;
      return {
        id: init._id || init.id,
        planId: init.planId ? (typeof init.planId === 'string' ? init.planId : (init.planId?._id || init.planId?.id)) : undefined,
        scorecardId: init.scorecardId ? (typeof init.scorecardId === 'string' ? init.scorecardId : (init.scorecardId?._id || init.scorecardId?.id)) : undefined,
        quarter: init.quarter,
        objectiveId: typeof init.objectiveId === 'string' 
          ? init.objectiveId 
          : (init.objectiveId?._id || init.objectiveId?.id || init.objectiveId),
        title: init.title,
        owner: init.owner,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/strategic-initiatives/${id}`);
    },
  },
  initiativeKpis: {
    getAll: async (initiativeId: string): Promise<InitiativeKPI[]> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/initiative-kpis?initiativeId=${initiativeId}`);
      const backendData = response.data?.data || response.data;
      const kpis = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return kpis.map((kpi: any) => ({
        id: kpi._id || kpi.id,
        initiativeId: typeof kpi.initiativeId === 'string' 
          ? kpi.initiativeId 
          : (kpi.initiativeId?._id || kpi.initiativeId?.id || kpi.initiativeId),
        name: kpi.name,
        baseline: kpi.baseline,
        target: kpi.target,
      }));
    },
    getById: async (id: string): Promise<InitiativeKPI> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/initiative-kpis/${id}`);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        initiativeId: typeof kpi.initiativeId === 'string' 
          ? kpi.initiativeId 
          : (kpi.initiativeId?._id || kpi.initiativeId?.id || kpi.initiativeId),
        name: kpi.name,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    create: async (data: {
      initiativeId: string;
      name: string;
      baseline: string;
      target: string;
    }): Promise<InitiativeKPI> => {
      const response = await api.post<any>('/old-strategic-performance-settings/initiative-kpis', data);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        initiativeId: typeof kpi.initiativeId === 'string' 
          ? kpi.initiativeId 
          : (kpi.initiativeId?._id || kpi.initiativeId?.id || kpi.initiativeId),
        name: kpi.name,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    update: async (id: string, data: {
      name?: string;
      baseline?: string;
      target?: string;
    }): Promise<InitiativeKPI> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/initiative-kpis/${id}`, data);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        initiativeId: typeof kpi.initiativeId === 'string' 
          ? kpi.initiativeId 
          : (kpi.initiativeId?._id || kpi.initiativeId?.id || kpi.initiativeId),
        name: kpi.name,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/initiative-kpis/${id}`);
    },
  },
  standardInitiatives: {
    getAll: async (planId?: string, scorecardId?: string, quarter?: string): Promise<StandardInitiative[]> => {
      const params = new URLSearchParams();
      if (planId) params.append('planId', planId);
      if (scorecardId) params.append('scorecardId', scorecardId);
      if (quarter) params.append('quarter', quarter);
      
      const response = await api.get<any>(`/old-strategic-performance-settings/standard-initiatives?${params.toString()}`);
      const backendData = response.data?.data || response.data;
      const initiatives = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return initiatives.map((init: any) => ({
        id: init._id || init.id,
        planId: init.planId ? (typeof init.planId === 'string' ? init.planId : (init.planId?._id || init.planId?.id)) : undefined,
        scorecardId: init.scorecardId ? (typeof init.scorecardId === 'string' ? init.scorecardId : (init.scorecardId?._id || init.scorecardId?.id)) : undefined,
        quarter: init.quarter,
        objectiveId: typeof init.objectiveId === 'string' 
          ? init.objectiveId 
          : (init.objectiveId?._id || init.objectiveId?.id || init.objectiveId),
        title: init.title,
        department: init.department,
      }));
    },
    getById: async (id: string): Promise<StandardInitiative> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/standard-initiatives/${id}`);
      const init = response.data?.data || response.data;
      return {
        id: init._id || init.id,
        planId: init.planId ? (typeof init.planId === 'string' ? init.planId : (init.planId?._id || init.planId?.id)) : undefined,
        scorecardId: init.scorecardId ? (typeof init.scorecardId === 'string' ? init.scorecardId : (init.scorecardId?._id || init.scorecardId?.id)) : undefined,
        quarter: init.quarter,
        objectiveId: typeof init.objectiveId === 'string' 
          ? init.objectiveId 
          : (init.objectiveId?._id || init.objectiveId?.id || init.objectiveId),
        title: init.title,
        department: init.department,
      };
    },
    create: async (data: {
      planId?: string;
      scorecardId?: string;
      quarter?: string;
      objectiveId: string;
      title: string;
      department: string;
    }): Promise<StandardInitiative> => {
      const response = await api.post<any>('/old-strategic-performance-settings/standard-initiatives', data);
      const init = response.data?.data || response.data;
      return {
        id: init._id || init.id,
        planId: init.planId ? (typeof init.planId === 'string' ? init.planId : (init.planId?._id || init.planId?.id)) : undefined,
        scorecardId: init.scorecardId ? (typeof init.scorecardId === 'string' ? init.scorecardId : (init.scorecardId?._id || init.scorecardId?.id)) : undefined,
        quarter: init.quarter,
        objectiveId: typeof init.objectiveId === 'string' 
          ? init.objectiveId 
          : (init.objectiveId?._id || init.objectiveId?.id || init.objectiveId),
        title: init.title,
        department: init.department,
      };
    },
    update: async (id: string, data: {
      objectiveId?: string;
      title?: string;
      department?: string;
    }): Promise<StandardInitiative> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/standard-initiatives/${id}`, data);
      const init = response.data?.data || response.data;
      return {
        id: init._id || init.id,
        planId: init.planId ? (typeof init.planId === 'string' ? init.planId : (init.planId?._id || init.planId?.id)) : undefined,
        scorecardId: init.scorecardId ? (typeof init.scorecardId === 'string' ? init.scorecardId : (init.scorecardId?._id || init.scorecardId?.id)) : undefined,
        quarter: init.quarter,
        objectiveId: typeof init.objectiveId === 'string' 
          ? init.objectiveId 
          : (init.objectiveId?._id || init.objectiveId?.id || init.objectiveId),
        title: init.title,
        department: init.department,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/standard-initiatives/${id}`);
    },
  },
  standardInitiativeKpis: {
    getAll: async (initiativeId: string): Promise<StandardInitiativeKPI[]> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/standard-initiative-kpis?initiativeId=${initiativeId}`);
      const backendData = response.data?.data || response.data;
      const kpis = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return kpis.map((kpi: any) => ({
        id: kpi._id || kpi.id,
        initiativeId: typeof kpi.initiativeId === 'string' 
          ? kpi.initiativeId 
          : (kpi.initiativeId?._id || kpi.initiativeId?.id || kpi.initiativeId),
        name: kpi.name,
        baseline: kpi.baseline,
        target: kpi.target,
      }));
    },
    getById: async (id: string): Promise<StandardInitiativeKPI> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/standard-initiative-kpis/${id}`);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        initiativeId: typeof kpi.initiativeId === 'string' 
          ? kpi.initiativeId 
          : (kpi.initiativeId?._id || kpi.initiativeId?.id || kpi.initiativeId),
        name: kpi.name,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    create: async (data: {
      initiativeId: string;
      name: string;
      baseline: string;
      target: string;
    }): Promise<StandardInitiativeKPI> => {
      const response = await api.post<any>('/old-strategic-performance-settings/standard-initiative-kpis', data);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        initiativeId: typeof kpi.initiativeId === 'string' 
          ? kpi.initiativeId 
          : (kpi.initiativeId?._id || kpi.initiativeId?.id || kpi.initiativeId),
        name: kpi.name,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    update: async (id: string, data: {
      name?: string;
      baseline?: string;
      target?: string;
    }): Promise<StandardInitiativeKPI> => {
      const response = await api.put<any>(`/old-strategic-performance-settings/standard-initiative-kpis/${id}`, data);
      const kpi = response.data?.data || response.data;
      return {
        id: kpi._id || kpi.id,
        initiativeId: typeof kpi.initiativeId === 'string' 
          ? kpi.initiativeId 
          : (kpi.initiativeId?._id || kpi.initiativeId?.id || kpi.initiativeId),
        name: kpi.name,
        baseline: kpi.baseline,
        target: kpi.target,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/standard-initiative-kpis/${id}`);
    },
  },
  
  // Assessment Frequency APIs
  assessmentFrequencies: {
    getAll: async (scorecardId: string): Promise<AssessmentFrequency[]> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/assessment-frequencies/${scorecardId}`);
      const backendData = response.data?.data || response.data;
      const frequencies = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return frequencies.map((freq: any) => ({
        id: freq._id || freq.id,
        scorecardId: typeof freq.scorecardId === 'string' 
          ? freq.scorecardId 
          : (freq.scorecardId?._id || freq.scorecardId?.id || freq.scorecardId),
        employeeId: typeof freq.employeeId === 'string' 
          ? freq.employeeId 
          : (freq.employeeId?._id || freq.employeeId?.id || freq.employeeId),
        frequency: freq.frequency,
      }));
    },
    createOrUpdate: async (data: {
      scorecardId: string;
      employeeId: string;
      frequency: 'Quarterly' | 'Bi-Annually' | 'Annually';
    }): Promise<AssessmentFrequency> => {
      const response = await api.post<any>('/old-strategic-performance-settings/assessment-frequencies', data);
      const freq = response.data?.data || response.data;
      return {
        id: freq._id || freq.id,
        scorecardId: typeof freq.scorecardId === 'string' 
          ? freq.scorecardId 
          : (freq.scorecardId?._id || freq.scorecardId?.id || freq.scorecardId),
        employeeId: typeof freq.employeeId === 'string' 
          ? freq.employeeId 
          : (freq.employeeId?._id || freq.employeeId?.id || freq.employeeId),
        frequency: freq.frequency,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/assessment-frequencies/${id}`);
    },
  },
  
  // Competency Setting APIs
  competencySettings: {
    getAll: async (scorecardId: string): Promise<Array<{ id: string; scorecardId: string; employeeId: string; contributionPercentage: number }>> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/competency-settings/${scorecardId}`);
      const backendData = response.data?.data || response.data;
      const settings = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return settings.map((setting: any) => ({
        id: setting._id || setting.id,
        scorecardId: typeof setting.scorecardId === 'string' 
          ? setting.scorecardId 
          : (setting.scorecardId?._id || setting.scorecardId?.id || setting.scorecardId),
        employeeId: typeof setting.employeeId === 'string' 
          ? setting.employeeId 
          : (setting.employeeId?._id || setting.employeeId?.id || setting.employeeId),
        contributionPercentage: setting.contributionPercentage,
      }));
    },
    createOrUpdate: async (data: {
      scorecardId: string;
      employeeId: string;
      contributionPercentage: number;
    }): Promise<{ id: string; scorecardId: string; employeeId: string; contributionPercentage: number }> => {
      const response = await api.post<any>('/old-strategic-performance-settings/competency-settings', data);
      const setting = response.data?.data || response.data;
      return {
        id: setting._id || setting.id,
        scorecardId: typeof setting.scorecardId === 'string' 
          ? setting.scorecardId 
          : (setting.scorecardId?._id || setting.scorecardId?.id || setting.scorecardId),
        employeeId: typeof setting.employeeId === 'string' 
          ? setting.employeeId 
          : (setting.employeeId?._id || setting.employeeId?.id || setting.employeeId),
        contributionPercentage: setting.contributionPercentage,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/competency-settings/${id}`);
    },
  },
  scorecardPeriodStatuses: {
    getAll: async (employeeScorecardId?: string): Promise<ScorecardPeriodStatus[]> => {
      const params = employeeScorecardId ? { employeeScorecardId } : {};
      const response = await api.get<any>('/old-strategic-performance-settings/scorecard-period-statuses', { params });
      const backendData = response.data?.data || response.data;
      const statuses = Array.isArray(backendData) ? backendData : (backendData?.data || []);
      return statuses.map((status: any) => ({
        id: status.id || status._id,
        employeeScorecardId: status.employeeScorecardId,
        period: status.period,
        supervisorId: status.supervisorId || null,
        status: status.status,
        submittedAt: status.submittedAt,
        approvedAt: status.approvedAt,
        approvedBy: status.approvedBy || null,
        sentBackAt: status.sentBackAt,
        sentBackBy: status.sentBackBy || null,
        sentBackComment: status.sentBackComment || '',
        modificationsAllowed: status.modificationsAllowed || false,
      }));
    },
    getById: async (id: string): Promise<ScorecardPeriodStatus> => {
      const response = await api.get<any>(`/old-strategic-performance-settings/scorecard-period-statuses/${id}`);
      const status = response.data?.data || response.data;
      return {
        id: status.id || status._id,
        employeeScorecardId: status.employeeScorecardId,
        period: status.period,
        supervisorId: status.supervisorId || null,
        status: status.status,
        submittedAt: status.submittedAt,
        approvedAt: status.approvedAt,
        approvedBy: status.approvedBy || null,
        sentBackAt: status.sentBackAt,
        sentBackBy: status.sentBackBy || null,
        sentBackComment: status.sentBackComment || '',
        modificationsAllowed: status.modificationsAllowed || false,
      };
    },
    createOrUpdate: async (data: {
      employeeScorecardId: string;
      period: string;
      supervisorId?: string | null;
      status?: 'draft' | 'submitted' | 'approved';
      submittedAt?: string;
      approvedAt?: string;
      approvedBy?: string | null;
      sentBackAt?: string;
      sentBackBy?: string | null;
      sentBackComment?: string;
      modificationsAllowed?: boolean;
    }): Promise<ScorecardPeriodStatus> => {
      const response = await api.post<any>('/old-strategic-performance-settings/scorecard-period-statuses', data);
      const status = response.data?.data || response.data;
      return {
        id: status.id || status._id,
        employeeScorecardId: status.employeeScorecardId,
        period: status.period,
        supervisorId: status.supervisorId || null,
        status: status.status,
        submittedAt: status.submittedAt,
        approvedAt: status.approvedAt,
        approvedBy: status.approvedBy || null,
        sentBackAt: status.sentBackAt,
        sentBackBy: status.sentBackBy || null,
        sentBackComment: status.sentBackComment || '',
        modificationsAllowed: status.modificationsAllowed || false,
      };
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`/old-strategic-performance-settings/scorecard-period-statuses/${id}`);
    },
  },
};

export const strategicPerformanceStateAPI = {
  getState: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<any>('/old-strategic-performance/state');
    const backendData = response.data?.data || response.data || {};
    return backendData;
  },
  setKey: async (key: string, value: unknown): Promise<unknown> => {
    const response = await api.put<any>(`/old-strategic-performance/state/${key}`, {
      value,
    });
    return response.data?.data ?? null;
  },
};
